import React, { Component } from "react"
import ComplianceTable from "../ComplianceTable"
import RangeFilter from "@swipesense/range-filter"
import { SortDirection } from "react-virtualized"
import PropTypes from "prop-types"
import natsort from "javascript-natural-sort"
import CSVButton from "@swipesense/csv-button"
import TableFooter from "../TableFooter"
import {
  getEntityTypeMapping,
  nestedEntityPath,
} from "../../helpers/sharedHelperFunctions"
import { complianceText } from "@swipesense/compliance-helpers"
import {
  ComplianceCircle,
  NOT_ENOUGH_DATA,
  NOT_ENOUGH_DATA_SORT_OFFSET,
} from "@swipesense/compliance-helpers"
import "./ComplianceTableWithControls.scss"
import momentPropTypes from "react-moment-proptypes"
import ExcludedAreas from "../ExcludedAreas"
import HelpTooltip from "../HelpTooltip"
import {
  NR_MIN_OPPORTUNITIES,
  BSR_MIN_OPPORTUNITIES,
} from "../../helpers/sharedConstants"
import BsrComplianceToggleWithStore from "../BsrComplianceToggle/BsrComplianceToggleWithStore"

class ComplianceTableWithControls extends Component {
  constructor(props) {
    super(props)

    this.initialComplianceFilterState = [
      { key: "all", text: "All Compliance", icon: null, selected: true },
      {
        key: "bucket1",
        text: "100-75%",
        icon: <ComplianceCircle compliance={75} />,
        selected: false,
      },
      {
        key: "bucket2",
        text: "75-50%",
        icon: <ComplianceCircle compliance={50} />,
        selected: false,
      },
      {
        key: "bucket3",
        text: "50-25%",
        icon: <ComplianceCircle compliance={25} />,
        selected: false,
      },
      {
        key: "bucket4",
        text: "25-0%",
        icon: <ComplianceCircle compliance={0} />,
        selected: false,
      },
      {
        key: "not-enough-data",
        text: "not enough data",
        icon: <ComplianceCircle compliance={NOT_ENOUGH_DATA} />,
        selected: false,
        children: (
          <HelpTooltip placement="right">
            For Nurse Rounding, "not enough data" appears when the number of
            opportunities (&lt;{NR_MIN_OPPORTUNITIES}) is not enough to
            calculate a statistically significant compliance - thereby
            equalizing performance evaluations. For Bedside Shift Report, the
            minimum number of opportunities is set to {BSR_MIN_OPPORTUNITIES}{" "}
            for now.
            <br />
            <br />
            Increase your date range to see a score with enough opportunities.
          </HelpTooltip>
        ),
      },
    ]

    this.onlyComplianceData = this.initialComplianceFilterState.map(option => {
      return {
        ...option,
        selected: !(option.key === "all" || option.key === "not-enough-data"),
      }
    })

    this.state = {
      complianceFilters: this.props.onlyComplianceData
        ? this.onlyComplianceData
        : this.initialComplianceFilterState,
      searchTerm: "",
      sortBy: "compliance",
      sortDirection: props.dataSort || SortDirection.DESC,
      excludedAreasModalOpen: false,
    }
  }

  componentDidMount() {
    // table-wrapper is created at render time, but we need its fully rendered height to apply our footer class.
    // Set it here so the footer renders only after the wrapper renders.
    if (this.tableWrapper) this.setState({ tableWrapper: this.tableWrapper })
  }

  handleSearchChange = e => {
    this.setState({ searchTerm: e.target.value })
  }

  handleComplianceFilterChange = filter => {
    if (filter === "all") {
      this.resetComplianceFilters()
    } else {
      this.updateComplianceFilters(filter)
    }
  }

  resetComplianceFilters = () => {
    this.setState({
      complianceFilters: this.initialComplianceFilterState,
    })
  }

  updateComplianceFilters = filter => {
    this.setState(prevState => {
      return {
        complianceFilters: prevState.complianceFilters.map(option => {
          let selected
          if (option.key === "all") selected = false
          else if (option.key === filter) selected = !option.selected
          else selected = option.selected

          return { ...option, selected: selected }
        }),
      }
    }, this.resetIfEmpty)
  }

  resetIfEmpty = () => {
    if (this.state.complianceFilters.every(option => !option.selected)) {
      this.resetComplianceFilters()
    }
  }

  handleSortChange = (sortBy, sortDirection) => {
    this.setState({
      sortBy,
      sortDirection,
    })
  }

  handleLink = entityMapping => {
    return props => {
      const path = nestedEntityPath({
        parentEntityType: this.props.parentEntityType || this.props.entityType,
        parentEntityId: this.props.parentEntityId || this.props.entityId,
        entityType: entityMapping.singularRoute,
        entityId: props[0],
      })

      this.props.updatePath(path)
    }
  }

  maybeRowClick = () => {
    const entityMapping = getEntityTypeMapping(this.props.childEntityType)
    const entityIndexTable =
      this.props.childEntityType &&
      this.props.entityType &&
      !this.props.parentEntityType
    const clickableNestedEntity = ["unit", "user", "location"].includes(
      entityMapping.singularField
    )

    if (entityIndexTable || clickableNestedEntity) {
      return this.handleLink(entityMapping)
    }
  }

  complianceSortValue = (rawValue, opportunities) => {
    if (
      opportunities <
      (this.props.bsrCompliance ? BSR_MIN_OPPORTUNITIES : NR_MIN_OPPORTUNITIES)
    ) {
      return this.state.sortDirection === "DESC"
        ? rawValue - NOT_ENOUGH_DATA_SORT_OFFSET
        : rawValue + NOT_ENOUGH_DATA_SORT_OFFSET
    } else {
      return rawValue
    }
  }

  changeSortValue = (rawValue, opportunities) => {
    /* istanbul ignore next */
    if (
      opportunities <
      (this.props.bsrCompliance ? BSR_MIN_OPPORTUNITIES : NR_MIN_OPPORTUNITIES)
    ) {
      return this.state.sortDirection === "DESC"
        ? -NOT_ENOUGH_DATA_SORT_OFFSET
        : NOT_ENOUGH_DATA_SORT_OFFSET
    } else if (rawValue === null) {
      // we always want null change to show up just above NED
      return this.state.sortDirection === "DESC"
        ? -NOT_ENOUGH_DATA_SORT_OFFSET + 1
        : NOT_ENOUGH_DATA_SORT_OFFSET - 1
    } else {
      return rawValue
    }
  }

  sortValue = rowData => {
    const cellValue = rowData[this.state.sortBy]

    if (this.state.sortBy === "compliance") {
      return this.complianceSortValue(cellValue, rowData.opportunities)
    } else if (this.state.sortBy === "change") {
      return this.changeSortValue(cellValue, rowData.opportunities)
    } else {
      return cellValue
    }
  }

  sortData = unsortedData => {
    natsort.insensitive = true

    const sortedData = unsortedData.sort((rowA, rowB) =>
      natsort(this.sortValue(rowA), this.sortValue(rowB))
    )

    return this.state.sortDirection !== SortDirection.DESC
      ? sortedData
      : sortedData.reverse()
  }

  filterData = unfilteredData => {
    let nameResults = []
    let subNameResults = []

    const selectedFilters = this.state.complianceFilters
      .filter(option => option.selected)
      .map(option => option.key)

    if (!(selectedFilters.length === 1 && selectedFilters[0] === "all")) {
      unfilteredData = unfilteredData.filter(result => {
        return selectedFilters.indexOf(result.bucket) !== -1
      })
    }

    nameResults = unfilteredData.filter(result => {
      return (
        result.name
          .toLowerCase()
          .indexOf(this.state.searchTerm.toLowerCase()) !== -1
      )
    })

    if (this.props.subName) {
      subNameResults = unfilteredData.filter(row => {
        const field = this.props.subName(row)
        return (
          field &&
          field.toLowerCase().indexOf(this.state.searchTerm.toLowerCase()) !==
            -1
        )
      })
    }

    // Create Set to remove duplicates
    const combinedResultSet = new Set(nameResults.concat(subNameResults))

    return Array.from(combinedResultSet)
  }

  csvFilename = () =>
    `${this.props.entityName.replace(/ /g, "_").toLowerCase()}_${
      this.props.childEntityType
    }_compliance.csv`

  csvHeaders = () => {
    const entityTypeMapping = getEntityTypeMapping(this.props.childEntityType)
    const lastColumns = [
      "period start",
      "period end",
      this.props.bsrCompliance ? "bsr compliance" : "compliance",
      "change",
      "opportunities",
    ]

    switch (entityTypeMapping.singularField) {
      case "user":
        return ["staff", "department", "role", ...lastColumns]
      case "location":
        return ["patient room", "unit", ...lastColumns]
      default:
        return [entityTypeMapping.singularDisplay.toLowerCase(), ...lastColumns]
    }
  }

  render() {
    const data = this.sortData(this.filterData(this.props.data))

    return (
      <div className="ComplianceTableWithControls">
        <div className="filter-search-wrapper">
          {this.props.loading ? null : (
            <div className="filter-and-search">
              <RangeFilter
                options={this.state.complianceFilters}
                handleChange={this.handleComplianceFilterChange}
              />
              {this.props.simpleDisplay || <BsrComplianceToggleWithStore />}
              <div className="search-and-download">
                <CSVButton
                  filename={this.csvFilename()}
                  headers={this.csvHeaders()}
                  data={data}
                  mapData={data => ({
                    ...data,
                    role: data.healthcareRole,
                    unit: data.unit && data.unit.name,
                    department: data.department && data.department.name,
                    [getEntityTypeMapping(
                      this.props.childEntityType
                    ).singularDisplay.toLowerCase()]: data.name,
                    "period start": this.props.startDate.format("M/D/YYYY"),
                    "period end": this.props.endDate.format("M/D/YYYY"),
                    [this.props.bsrCompliance
                      ? "bsr compliance"
                      : "compliance"]:
                      /* istanbul ignore next */
                      data.opportunities <
                      (this.props.bsrCompliance
                        ? BSR_MIN_OPPORTUNITIES
                        : NR_MIN_OPPORTUNITIES)
                        ? `not enough data (${Math.round(data.compliance)}%)`
                        : complianceText(
                            data.compliance,
                            data.opportunities,
                            this.props.bsrCompliance
                              ? BSR_MIN_OPPORTUNITIES
                              : NR_MIN_OPPORTUNITIES
                          ),
                    change:
                      /* istanbul ignore next */
                      data.change &&
                      data.opportunities >=
                        (this.props.bsrCompliance
                          ? BSR_MIN_OPPORTUNITIES
                          : NR_MIN_OPPORTUNITIES)
                        ? `${Math.round(data.change)}%`
                        : "-",
                    opportunities: `${data.opportunities}`, // make it a string for consistency
                  })}
                />
              </div>
            </div>
          )}
        </div>
        <div className="table-wrapper" ref={el => (this.tableWrapper = el)}>
          <ComplianceTable
            data={data}
            onRowClick={this.maybeRowClick()}
            sortBy={this.state.sortBy}
            sortDirection={this.state.sortDirection}
            onSortChange={this.handleSortChange}
            updatePath={this.props.updatePath}
            subName={this.props.subName}
            loading={this.props.loading}
            nameLabel={
              getEntityTypeMapping(this.props.childEntityType).singularDisplay
            }
            bsrCompliance={this.props.bsrCompliance}
          />
        </div>
        <div>
          <TableFooter
            dataCount={data.length}
            wrapperHeight={
              this.state.tableWrapper &&
              this.state.tableWrapper.getClientRects()[0].height
            }
            rowHeight={this.props.subName ? 46 : 36}
          >
            {["locations", "units"].includes(this.props.childEntityType) && (
              <span
                className="excluded-areas footer-link"
                onClick={() =>
                  this.setState({
                    excludedAreasModalOpen: true,
                  })
                }
              >
                <span>Excluded Areas</span>
              </span>
            )}
          </TableFooter>
          <ExcludedAreas
            modalOpen={this.state.excludedAreasModalOpen}
            onClose={() => this.setState({ excludedAreasModalOpen: false })}
          />
        </div>
      </div>
    )
  }
}

ComplianceTableWithControls.propTypes = {
  parentEntityType: PropTypes.string,
  parentEntityId: PropTypes.string,
  entityName: PropTypes.string.isRequired,
  entityId: PropTypes.string.isRequired,
  childEntityType: PropTypes.string.isRequired,
  data: ComplianceTable.propTypes.data,
  subName: PropTypes.func,
  onlyComplianceData: PropTypes.bool,
  dataSort: PropTypes.string,
  updatePath: PropTypes.func,
  loading: PropTypes.bool,
  entityType: PropTypes.string,
  startDate: momentPropTypes.momentObj,
  endDate: momentPropTypes.momentObj,
  bsrCompliance: PropTypes.bool,
  simpleDisplay: PropTypes.bool,
}

ComplianceTableWithControls.defaultProps = {
  entityName: "swipesense",
}

export default ComplianceTableWithControls
